/**
 * Hook que encapsula a lógica de submit do `FormRenderer`:
 *   - Filtra respostas por visibilidade (`visible_if`)
 *   - Valida via Zod dinâmico
 *   - Mapeia erros para `{ fieldId: message }`
 *   - Rola até o primeiro erro em mobile
 *   - Chama `onSubmit` em caso de sucesso
 *
 * Extraído do `FormRenderer` para respeitar limite de 200 linhas
 * por componente (AGENTS.md §5.1).
 */
import { useCallback, useRef, useState, type FormEvent } from 'react';
import type { ZodTypeAny } from 'zod';

import type { FormField } from '../../schemas.js';
import { isVisible } from '../logic/visibleIf.js';

export interface UseFormSubmitArgs {
  allFields: Map<string, FormField>;
  answers: Record<string, unknown>;
  validator: ZodTypeAny;
  onSubmit: (answers: Record<string, unknown>) => Promise<void> | void;
  idPrefix: string;
  disabled: boolean;
}

export interface UseFormSubmitResult {
  submitting: boolean;
  errors: Record<string, string>;
  setErrors: (
    next: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>),
  ) => void;
  handleSubmit: (event: FormEvent) => void;
}

export function useFormSubmit({
  allFields,
  answers,
  validator,
  onSubmit,
  idPrefix,
  disabled,
}: UseFormSubmitArgs): UseFormSubmitResult {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // Guard SÍNCRONO contra double-click. `submitting` (useState) atualiza
  // no próximo render — entre dois clicks rápidos (~16ms apart), o guard
  // baseado em state ainda vê `false` nos dois callbacks e deixa passar
  // as duas requisições. O ref muda instantaneamente, cortando o 2º
  // callback antes de disparar onSubmit. Bug 2026-04-24: Alexandre
  // clicou "Enviar" 2x porque a tela não trocou e bateu 409
  // already_submitted.
  const submittingRef = useRef(false);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (disabled || submittingRef.current || submitting) return;

      // Remove respostas de campos escondidos antes de validar —
      // campo oculto com required não deve bloquear submit.
      const visibleAnswers: Record<string, unknown> = {};
      for (const [id, field] of allFields.entries()) {
        if (isVisible(field.visible_if, answers)) {
          visibleAnswers[id] = answers[id];
        }
      }

      const parsed = validator.safeParse(visibleAnswers);
      if (!parsed.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const path = issue.path[0];
          if (typeof path === 'string' && !fieldErrors[path]) {
            fieldErrors[path] = issue.message;
          }
        }
        setErrors(fieldErrors);

        // Rola até o primeiro erro. `scrollIntoView` nem sempre existe
        // (jsdom em testes) — guard explícito evita unhandled rejection.
        const firstErrorId = Object.keys(fieldErrors)[0];
        if (firstErrorId) {
          const el = document.getElementById(`${idPrefix}-${firstErrorId}`);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      submittingRef.current = true;
      setSubmitting(true);
      setErrors({});
      try {
        await onSubmit(parsed.data as Record<string, unknown>);
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [allFields, answers, validator, onSubmit, idPrefix, disabled, submitting],
  );

  return { submitting, errors, setErrors, handleSubmit };
}
